import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AppConfigService } from 'src/app/services/app-config/app-config.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from 'src/app/services/auth/auth.service';
import * as alertifyjs from 'alertifyjs';
import { checkHomeModalImage, HomeModalImageInfo, HOME_MODAL_IMAGE_ACCEPT } from './home-modal-image.validator';
import {
    HomeModalAction,
    HomeModalActionType,
    HOME_MODAL_INTERNAL_ROUTES,
    describeHomeModalTap,
    toHomeModalActionType,
    validateHomeModalAction
} from './home-modal-action';

/** Same permission that gates the other switches of this screen. */
const HOME_MODAL_EDIT_PERMISSION = 'config.app_movil.config';

/** `data` of GET /api/app-config/get_home_modal_config, as kredit-api-MT sends it. */
interface HomeModalConfigPayload {
    enabled?: boolean;
    image_url?: string;
    action_type?: string;
    action_target?: string;
    image_updated_at?: string | null;
    image_updated_by?: string | null;
}

@Component({
    selector: 'app-app-config',
    templateUrl: './app-config.component.html',
    styleUrls: ['./app-config.component.css', './app-phone.css']
})
export class AppConfigComponent implements OnInit, OnDestroy {
    // 'services', 'simon', 'register', 'carrusel' (its own component, DK-53) or 'homeModal'
    activeTab: string = 'services';

    // Services tab
    services: any[] = [];
    rootServices: any[] = [];
    servicesLoading: boolean = false;

    // Add service form
    showAddServiceModal: boolean = false;
    addServiceForm: FormGroup;
    addServiceLoading: boolean = false;

    // SIMON tab
    showSimon: boolean = false;
    simonLoading: boolean = false;
    simonToggleLoading: boolean = false;

    // Register tab
    registerEnabled: boolean = false;
    registerMessage: string = '';
    registerLoading: boolean = false;
    registerToggleLoading: boolean = false;
    registerMessageSaving: boolean = false;

    // Modal Home App tab
    homeModalEnabled: boolean = false;
    homeModalLoading: boolean = false;
    homeModalToggleLoading: boolean = false;
    homeModalContentSaving: boolean = false;

    // Modal Home App — content as last loaded from the backend
    homeModalImageUrl: string = '';
    homeModalImageUpdatedAt: string | null = null;
    homeModalImageUpdatedBy: string | null = null;
    homeModalLoadedActionType: HomeModalActionType = '';
    homeModalLoadedActionTarget: string = '';

    // Modal Home App — operator draft (new image + tap action), compared against the loaded content
    homeModalActionType: HomeModalActionType = '';
    homeModalInternalRoute: string = '';
    homeModalExternalUrl: string = '';
    homeModalSelectedFile: File | null = null;
    homeModalSelectedImage: HomeModalImageInfo | null = null;
    homeModalSelectedImageError: string = '';
    homeModalImageChecking: boolean = false;
    homeModalSelectedPreview: SafeUrl | null = null;
    private homeModalSelectedObjectUrl: string = '';
    readonly homeModalInternalRoutes = HOME_MODAL_INTERNAL_ROUTES;
    readonly homeModalImageAccept = HOME_MODAL_IMAGE_ACCEPT;

    constructor(
        private appConfigService: AppConfigService,
        private formBuilder: FormBuilder,
        private cdr: ChangeDetectorRef,
        private sanitizer: DomSanitizer,
        public _auth: AuthService
    ) {
        this.addServiceForm = this.formBuilder.group({
            code: ['', [Validators.required]],
            name: ['', [Validators.required]],
            description: [''],
            order: [1, [Validators.required, Validators.min(1)]],
            parent: ['']
        });
    }

    ngOnInit(): void {
        this.loadServices();
        this.loadShowSimon();
        this.loadRegisterConfig();
        this.loadHomeModalConfig();
    }

    ngOnDestroy(): void {
        this.clearHomeModalSelectedFile();
    }

    /**
     * Load available services
     */
    loadServices = async () => {
        try {
            this.servicesLoading = true;
            const resp = await this.appConfigService.getAvailableServices();

            // Try different possible response structures
            let servicesArray = null;

            if (Array.isArray(resp)) {
                servicesArray = resp;
            } else if (resp?.data && Array.isArray(resp.data)) {
                servicesArray = resp.data;
            } else if (resp?.services && Array.isArray(resp.services)) {
                servicesArray = resp.services;
            } else if (resp?.data?.services && Array.isArray(resp.data.services)) {
                servicesArray = resp.data.services;
            } else if (resp?.result && Array.isArray(resp.result)) {
                servicesArray = resp.result;
            } else {
                console.warn('Could not find services array in response');
                servicesArray = [];
            }

            // Normalize services - convert 'status' field to 'enabled' boolean
            this.services = servicesArray.map((service: any) => {
                // The API uses 'status' field - convert to boolean 'enabled'
                let enabled = false;

                if (service.status !== undefined) {
                    if (typeof service.status === 'string') {
                        enabled = service.status.toLowerCase() === 'active';
                    } else if (typeof service.status === 'boolean') {
                        enabled = service.status;
                    } else if (typeof service.status === 'number') {
                        enabled = service.status === 1;
                    }
                }

                return {
                    ...service,
                    enabled: enabled,
                    children: [] // Initialize children array
                };
            });

            this.buildServiceHierarchy();
            console.log('Services loaded:', this.services.length, 'services');
            console.log('Root services:', this.rootServices.length);

        } catch (error) {
            console.error('Error loading services:', error);
            alertifyjs.error('Error al cargar los servicios');
            this.services = [];
            this.rootServices = [];
        } finally {
            this.servicesLoading = false;
        }
    };

    /**
     * Build service hierarchy tree
     */
    buildServiceHierarchy() {
        const serviceMap = new Map();
        this.rootServices = [];

        // First pass: Map services by code
        this.services.forEach(service => {
            service.children = []; // Reset children
            serviceMap.set(service.code, service);
        });

        // Second pass: Organize into tree
        this.services.forEach(service => {
            if (service.parent && serviceMap.has(service.parent)) {
                const parent = serviceMap.get(service.parent);
                parent.children.push(service);
            } else {
                // If no parent or parent not found, it's a root service
                this.rootServices.push(service);
            }
        });
    }

    /**
     * Toggle service enabled/disabled
     */
    /**
     * Toggle service enabled/disabled
     */
    toggleService = async (service: any, event: any) => {
        if (service.loading) {
            event.preventDefault();
            return;
        }

        const checkbox = event.target;
        const newValue = checkbox.checked;

        await this.updateServiceStatus(service, newValue, checkbox);
    };

    /**
     * Internal method to update service status
     */
    updateServiceStatus = async (service: any, newValue: boolean, checkbox?: any) => {
        try {
            service.loading = true;

            // Use the toggle-status endpoint
            const resp = await this.appConfigService.toggleServiceStatus(service.code, newValue);

            if (resp) {
                service.enabled = newValue;
                service.status = newValue ? 'active' : 'inactive';

                if (checkbox) {
                    // Only show alert if triggered by user interaction
                    alertifyjs.success(newValue ? 'Servicio activado' : 'Servicio desactivado');
                }

                // Check parent status if this is a child service
                if (service.parent) {
                    await this.checkParentStatus(service);
                }
            } else {
                if (checkbox) checkbox.checked = !newValue;
                service.enabled = !newValue;
                alertifyjs.error('Error al actualizar el servicio');
            }
        } catch (error) {
            console.error('Error toggling service:', error);
            alertifyjs.error('Error al actualizar el servicio');
            if (checkbox) checkbox.checked = !newValue;
            service.enabled = !newValue;
        } finally {
            service.loading = false;
        }
    };

    /**
     * Check and update parent status based on children
     */
    checkParentStatus = async (childService: any) => {
        // Find parent service
        const parent = this.services.find(s => s.code === childService.parent);

        if (!parent) return;

        // Get all children of this parent
        const children = this.services.filter(s => s.parent === parent.code);

        // Check if any child is active
        const anyChildActive = children.some(child => child.enabled);

        // Logic:
        // 1. If all children are inactive -> Inactive parent
        // 2. If at least one child is active -> Active parent

        if (anyChildActive && !parent.enabled) {
            // Activate parent
            console.log('Activating parent service:', parent.name);
            await this.updateServiceStatus(parent, true);
        } else if (!anyChildActive && parent.enabled) {
            // Deactivate parent
            console.log('Deactivating parent service:', parent.name);
            await this.updateServiceStatus(parent, false);
        }
    };

    /**
     * Open add service modal
     */
    openAddServiceModal = () => {
        this.showAddServiceModal = true;
        this.addServiceForm.reset({
            code: '',
            name: '',
            description: '',
            order: 1,
            parent: ''
        });
    };

    /**
     * Close add service modal
     */
    closeAddServiceModal = () => {
        this.showAddServiceModal = false;
    };

    /**
     * Submit add service form
     */
    submitAddService = async () => {
        if (this.addServiceForm.valid) {
            try {
                this.addServiceLoading = true;
                const formData = this.addServiceForm.getRawValue();

                const resp = await this.appConfigService.addService(formData);

                if (resp) {
                    alertifyjs.success('Servicio agregado exitosamente');
                    this.closeAddServiceModal();
                    await this.loadServices();
                } else {
                    alertifyjs.error('Error al agregar el servicio');
                }
            } catch (error: any) {
                console.error('Error adding service:', error);
                const errorMsg = error?.error?.msg || error?.error?.message || 'Error al agregar el servicio';
                alertifyjs.error(errorMsg);
            } finally {
                this.addServiceLoading = false;
            }
        } else {
            alertifyjs.error('Por favor complete todos los campos requeridos');
        }
    };

    /**
     * Delete service with confirmation
     */
    /**
     * Delete service with confirmation
     */
    deleteService = async (service: any) => {
        let confirmMessage = `¿Está seguro que desea eliminar el servicio "${service.name}"?`;

        if (service.children && service.children.length > 0) {
            confirmMessage = `¿Está seguro que desea eliminar el servicio "${service.name}"?\n\nADVERTENCIA: Esto también eliminará sus ${service.children.length} sub-servicios asociados.`;
        }

        const confirmed = confirm(confirmMessage);

        if (!confirmed) {
            return;
        }

        try {
            service.deleting = true;

            // If it has children, delete them first
            if (service.children && service.children.length > 0) {
                for (const child of service.children) {
                    try {
                        await this.appConfigService.deleteService(child.code);
                    } catch (error) {
                        console.error(`Error deleting child service ${child.code}:`, error);
                        // Continue deleting other children and parent even if one fails
                    }
                }
            }

            // Delete the parent service
            const resp = await this.appConfigService.deleteService(service.code);

            if (resp) {
                alertifyjs.success('Servicio eliminado exitosamente');
                await this.loadServices();
            } else {
                alertifyjs.error('Error al eliminar el servicio');
            }
        } catch (error: any) {
            console.error('Error deleting service:', error);
            const errorMsg = error?.error?.msg || error?.error?.message || 'Error al eliminar el servicio';
            alertifyjs.error(errorMsg);
        } finally {
            service.deleting = false;
        }
    };

    /**
     * Load SIMON configuration
     */
    loadShowSimon = async () => {
        try {
            this.simonLoading = true;
            const resp = await this.appConfigService.getShowSimon();

            let value: any;

            if (resp?.data?.show_simon !== undefined) {
                value = resp.data.show_simon;
            } else if (resp?.show_simon !== undefined) {
                value = resp.show_simon;
            } else {
                value = false;
            }

            // Convert to boolean explicitly
            if (value === 'false' || value === false || value === 0 || value === '0') {
                this.showSimon = false;
            } else if (value === 'true' || value === true || value === 1 || value === '1') {
                this.showSimon = true;
            } else {
                this.showSimon = false;
            }

            console.log('showSimon set to:', this.showSimon);
            this.cdr.detectChanges();
        } catch (error) {
            console.error('Error loading show simon:', error);
            this.showSimon = false;
            this.cdr.detectChanges();
        } finally {
            this.simonLoading = false;
        }
    };

    /**
     * Toggle SIMON visibility
     */
    onSimonToggleChange = async (event: any) => {
        if (this.simonToggleLoading) {
            event.preventDefault();
            return;
        }

        const checkbox = event.target;
        const newValue = checkbox.checked;

        try {
            this.simonToggleLoading = true;

            const resp = await this.appConfigService.updateShowSimon(newValue);

            if (resp) {
                this.showSimon = newValue;
                alertifyjs.success(newValue ? 'SIMON chat activado en la app' : 'SIMON chat desactivado en la app');
            } else {
                checkbox.checked = !newValue;
                this.showSimon = !newValue;
                alertifyjs.error('Error al actualizar la configuración');
            }
        } catch (error) {
            console.error('Error updating show simon:', error);
            alertifyjs.error('Error al actualizar la configuración');
            checkbox.checked = !newValue;
            this.showSimon = !newValue;
        } finally {
            this.simonToggleLoading = false;
        }
    };

    /**
     * Load Register configuration
     */
    loadRegisterConfig = async () => {
        try {
            this.registerLoading = true;
            const resp = await this.appConfigService.getRegisterConfig();

            let enabled = false;
            let message = '';

            if (resp?.data?.enable !== undefined) {
                enabled = resp.data.enable;
            } else if (resp?.enable !== undefined) {
                enabled = resp.enable;
            }

            if (resp?.data?.text !== undefined) {
                message = resp.data.text;
            } else if (resp?.text !== undefined) {
                message = resp.text;
            }

            // Convert to boolean explicitly
            if (typeof enabled === 'string') {
                enabled = enabled === 'true' || enabled === '1';
            } else if (typeof enabled === 'number') {
                enabled = enabled === 1;
            }

            this.registerEnabled = !!enabled;
            this.registerMessage = message || '';
            console.log('registerEnabled set to:', this.registerEnabled);
            console.log('registerMessage set to:', this.registerMessage);
            this.cdr.detectChanges();
        } catch (error) {
            console.error('Error loading register config:', error);
            this.registerEnabled = false;
            this.registerMessage = '';
            this.cdr.detectChanges();
        } finally {
            this.registerLoading = false;
        }
    };

    /**
     * Toggle Register enable/disable
     */
    onRegisterToggleChange = async (event: any) => {
        if (this.registerToggleLoading) {
            event.preventDefault();
            return;
        }

        const checkbox = event.target;
        const newValue = checkbox.checked;

        try {
            this.registerToggleLoading = true;

            const resp = await this.appConfigService.toggleRegisterStatus(newValue);

            if (resp) {
                this.registerEnabled = newValue;
                alertifyjs.success(newValue ? 'Registro de usuarios activado' : 'Registro de usuarios desactivado');
            } else {
                checkbox.checked = !newValue;
                this.registerEnabled = !newValue;
                alertifyjs.error('Error al actualizar la configuración');
            }
        } catch (error) {
            console.error('Error updating register status:', error);
            alertifyjs.error('Error al actualizar la configuración');
            checkbox.checked = !newValue;
            this.registerEnabled = !newValue;
        } finally {
            this.registerToggleLoading = false;
        }
    };

    /**
     * Save Register message
     */
    saveRegisterMessage = async () => {
        try {
            this.registerMessageSaving = true;

            const resp = await this.appConfigService.updateRegisterMessage(this.registerMessage);

            if (resp) {
                alertifyjs.success('Mensaje actualizado exitosamente');
            } else {
                alertifyjs.error('Error al actualizar el mensaje');
            }
        } catch (error) {
            console.error('Error saving register message:', error);
            alertifyjs.error('Error al actualizar el mensaje');
        } finally {
            this.registerMessageSaving = false;
        }
    };

    /**
     * Load Modal Home configuration (status, image live in the app and tap action)
     */
    loadHomeModalConfig = async () => {
        try {
            this.homeModalLoading = true;
            const resp = await this.appConfigService.getHomeModalConfig();
            this.applyHomeModalConfig(resp?.data);
        } catch (error) {
            console.error('Error loading home modal config:', error);
            this.applyHomeModalConfig(null);
        } finally {
            this.homeModalLoading = false;
            this.cdr.detectChanges();
        }
    };

    /**
     * Copy the backend payload into the loaded state and align the draft with it
     */
    private applyHomeModalConfig(data: HomeModalConfigPayload | null | undefined): void {
        this.homeModalEnabled = !!data?.enabled;
        this.homeModalImageUrl = typeof data?.image_url === 'string' ? data.image_url : '';
        this.homeModalImageUpdatedAt = data?.image_updated_at || null;
        this.homeModalImageUpdatedBy = data?.image_updated_by || null;
        this.homeModalLoadedActionType = toHomeModalActionType(data?.action_type);
        this.homeModalLoadedActionTarget = typeof data?.action_target === 'string' ? data.action_target : '';
        this.resetHomeModalDraft();
    }

    /**
     * Toggle Modal Home enable/disable
     */
    onHomeModalToggleChange = async (event: any) => {
        if (this.homeModalToggleLoading) {
            event.preventDefault();
            return;
        }

        const checkbox = event.target;
        const newValue = checkbox.checked;

        try {
            this.homeModalToggleLoading = true;

            const resp = await this.appConfigService.toggleHomeModalStatus(newValue);

            if (resp) {
                this.homeModalEnabled = newValue;
                alertifyjs.success(newValue ? 'Modal home activado' : 'Modal home desactivado');
            } else {
                checkbox.checked = !newValue;
                this.homeModalEnabled = !newValue;
                alertifyjs.error('Error al actualizar la configuración');
            }
        } catch (error) {
            console.error('Error updating home modal status:', error);
            alertifyjs.error('Error al actualizar la configuración');
            checkbox.checked = !newValue;
            this.homeModalEnabled = !newValue;
        } finally {
            this.homeModalToggleLoading = false;
        }
    };

    /**
     * Operators without the config permission see the modal home content read-only
     */
    get canEditHomeModal(): boolean {
        return this._auth.hasPermission(HOME_MODAL_EDIT_PERMISSION);
    }

    /**
     * Operator picked a file: validate it client-side before enabling the publish button
     */
    onHomeModalFileSelected = async (event: Event) => {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] ?? null;
        // The input is emptied at once: the draft owns the file, and picking the same file again still fires `change`.
        input.value = '';
        if (!file) return;

        this.clearHomeModalSelectedFile();
        this.homeModalImageChecking = true;
        const objectUrl = URL.createObjectURL(file);
        try {
            const check = await checkHomeModalImage(file, objectUrl);
            this.homeModalSelectedFile = file;
            this.homeModalSelectedImage = check.info;
            this.homeModalSelectedImageError = check.error;
            if (check.error) {
                URL.revokeObjectURL(objectUrl);
            } else {
                this.homeModalSelectedObjectUrl = objectUrl;
                this.homeModalSelectedPreview = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
            }
        } finally {
            this.homeModalImageChecking = false;
            this.cdr.detectChanges();
        }
    };

    /**
     * Drop the selected file and release its object URL
     */
    clearHomeModalSelectedFile = () => {
        if (this.homeModalSelectedObjectUrl) {
            URL.revokeObjectURL(this.homeModalSelectedObjectUrl);
        }
        this.homeModalSelectedObjectUrl = '';
        this.homeModalSelectedPreview = null;
        this.homeModalSelectedFile = null;
        this.homeModalSelectedImage = null;
        this.homeModalSelectedImageError = '';
    };

    /**
     * «Cancelar»: discard the selected file and put the action back to the loaded values
     */
    resetHomeModalDraft = () => {
        this.clearHomeModalSelectedFile();
        this.homeModalActionType = this.homeModalLoadedActionType;
        this.homeModalInternalRoute = this.homeModalLoadedActionType === 'internal' ? this.homeModalLoadedActionTarget : '';
        this.homeModalExternalUrl = this.homeModalLoadedActionType === 'external' ? this.homeModalLoadedActionTarget : '';
    };

    /**
     * Action as the backend expects it; target is empty when there is no action
     */
    get homeModalDraftAction(): HomeModalAction {
        switch (this.homeModalActionType) {
            case 'internal':
                return { type: 'internal', target: this.homeModalInternalRoute };
            case 'external':
                return { type: 'external', target: this.homeModalExternalUrl.trim() };
            default:
                return { type: '', target: '' };
        }
    }

    /**
     * Inline error for the action block; empty when the action can be published
     */
    get homeModalActionError(): string {
        return validateHomeModalAction(this.homeModalDraftAction);
    }

    /**
     * Something to publish: a new file, or an action different from the loaded one
     */
    get homeModalHasChanges(): boolean {
        if (this.homeModalSelectedFile) return true;
        const draft = this.homeModalDraftAction;
        return draft.type !== this.homeModalLoadedActionType || draft.target !== this.homeModalLoadedActionTarget;
    }

    /**
     * «Confirmar y publicar» needs permission, nothing in flight, a valid draft and real changes
     */
    get canPublishHomeModal(): boolean {
        return this.canEditHomeModal
            && !this.homeModalContentSaving
            && !this.homeModalImageChecking
            && !this.homeModalSelectedImageError
            && !this.homeModalActionError
            && this.homeModalHasChanges;
    }

    /**
     * What the phone preview shows: the file just picked, else the image live in the app
     */
    get homeModalPreviewImage(): SafeUrl | string | null {
        return this.homeModalSelectedPreview || this.homeModalImageUrl || null;
    }

    /**
     * Hint under the preview describing what a tap does
     */
    get homeModalTapHint(): string {
        return describeHomeModalTap(this.homeModalDraftAction);
    }

    /**
     * «Confirmar y publicar»: one multipart request with the new image (if any) and the tap action
     */
    publishHomeModalContent = async () => {
        if (!this.canPublishHomeModal) return;

        const action = this.homeModalDraftAction;
        const formData = new FormData();
        if (this.homeModalSelectedFile) {
            formData.append('image', this.homeModalSelectedFile, this.homeModalSelectedFile.name);
        }
        formData.append('action_type', action.type);
        formData.append('action_target', action.target);

        try {
            this.homeModalContentSaving = true;
            const resp = await this.appConfigService.updateHomeModalContent(formData);
            if (!resp?.ok) {
                alertifyjs.error(resp?.message || 'No se pudo actualizar el modal home');
                return;
            }
            alertifyjs.success('Modal home actualizado. Se verá en la app en hasta 5 minutos');
            this.clearHomeModalSelectedFile();
            await this.loadHomeModalConfig();
        } catch (error: any) {
            console.error('Error publishing home modal content:', error);
            alertifyjs.error(error?.error?.message || 'No se pudo actualizar el modal home. Intenta de nuevo');
        } finally {
            this.homeModalContentSaving = false;
            this.cdr.detectChanges();
        }
    };

    /**
     * Change active tab
     */
    changeTab(tab: string) {
        this.activeTab = tab;
    }
}
